/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot } from '@angular/router';
import { OAuth2Tokens, SessionManager, TokenStorage } from '@forgerock/javascript-sdk';
import { Store } from '@ngrx/store';
import { filter, first, firstValueFrom, map, of } from 'rxjs';

import { env } from '@amfam/environment';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { LoadingSpinnerService } from '@amfam/ui-kit';

import { forgerockLogin } from '../store/session/forgerock-session.actions';
import { ForgerockAuthService } from './forgerock-auth.service';

@Injectable()
export class ForgerockAuthGuard {
  private emailValidationPath: string;

  constructor(
    private store: Store,
    private router: Router,
    private forgerockAuthService: ForgerockAuthService,
    private featureFlagService: FeatureFlagService,
    private analyticsFacade: AnalyticsFacade,
    private spinner: LoadingSpinnerService,
    private copyService: CopyService
  ) {
    this.emailValidationPath = env.profileValidateEmailPath;
  }
  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    if (!this.featureFlagService.isEnabled('forgerock')) {
      return of(true);
    }

    const url = state.url;

    // Deep link from mobile webview
    if (this.featureFlagService.isEnabled('policyview_webview')) {
      const webview = url.includes('webview');
      if (webview) {
        console.log('web view');
        let userId = this.getUserId(route);
        if (userId) {
          console.log('with userid');
          const session = await this.forgerockAuthService.hasSession();
          console.log('SESSION before session', session);
          window.location.href = `${this.forgerockAuthService.getPartnerUrl()}XUI/?realm=/alpha&authIndexType=service&authIndexValue=Login&goto=${location.origin}${url}`;
          return false;
        } else {
          console.log('back with session');
          const session = await this.forgerockAuthService.hasSession();
          console.log('SESSION after session', session);
          this.forgerockAuthService.redirectUrl = url;
          const acrValues = env.forgerock.login;
          const tokens = await this.forgerockAuthService.getTokens(acrValues, false);
          if (tokens) this.dispatchToAuthenticate(tokens as OAuth2Tokens);
          return firstValueFrom(
            this.forgerockAuthService.userLoaded$.pipe(
              filter(loaded => !!loaded),
              first(),
              map(() => true)
            )
          );
        }
      }
    }

    return this.checkAuth(url);
  }

  canLoad(route: Route) {
    const url = route.path;
    return this.checkAuth(url);
  }

  private getUserId(route: ActivatedRouteSnapshot) {
    return route.queryParamMap.get('userid');
  }

  private async checkAuth(url: string) {
    // redirect to intended URL after logging in
    this.forgerockAuthService.redirectUrl = url;
    if (this.forgerockAuthService.isLoggedIn()) {
      return true;
    }

    // If altAuth verified, return true to allow email validation
    if (this.forgerockAuthService.isAltAuthVerified) {
      if (url === '/profile') {
        return this.sendToLogin(url);
      }
      this.forgerockAuthService.redirectUrl = '/';
      return true;
    }

    try {
      const authenticated = await firstValueFrom(this.forgerockAuthService.refresh());
      if (authenticated) {
        console.log('refresh passed');
        switch (localStorage.getItem('frFlowType')) {
          case 'change-password':
            const hasTokens = await this.hasTokens();
            if (!hasTokens) await this.forgerockAuthService.getTokens();
            if (url.includes('/profile')) return true;
            else
              this.store.dispatch(
                fromRouterActions.Go({
                  path: ['/profile'],
                  query: { openAccordion: 'security' }
                })
              );
            break;

          case 'enrollment':
            if (url.includes('/paperless/sign-up')) return true;
            else this.store.dispatch(fromRouterActions.Go({ path: ['/paperless/sign-up'] }));
            break;

          default:
        }

        return true;
      } else {
        console.log('refresh failed');
        if (url.includes(this.emailValidationPath)) {
          console.log('email validation');
          return this.sendToEmailValidation(url);
        }
      }
    } catch (error) {
      console.log('refresh failed');
      if (url.includes(this.emailValidationPath)) {
        console.log('email validation');
        return this.sendToEmailValidation(url);
      }
    }

    let tokens;
    try {
      tokens = await this.forgerockAuthService.getTokens();
      if (tokens) {
        console.log('got tokens !');
        this.analyticsFacade.trackEvent({
          event: 'authenticate_sign_in_success'
        });
        return this.dispatchToAuthenticate(tokens as OAuth2Tokens);
      } else {
        console.log('no tokens from getTokens call');
        tokens = await TokenStorage.get();
        const tokenExpiryDate = new Date((tokens as OAuth2Tokens)?.tokenExpiry);
        if (new Date() > tokenExpiryDate) {
          await TokenStorage.remove();
          tokens = null;
        }

        if (tokens) {
          console.log('got tokens from storage');
          this.analyticsFacade.trackEvent({
            event: 'authenticate_sign_in_success'
          });
          return this.dispatchToAuthenticate(tokens as OAuth2Tokens);
        } else {
          console.log('no tokens from storage');
          // User likely not authenticated
          this.analyticsFacade.trackEvent({
            event: 'authenticate_sign_in_failure'
          });
          if (url.includes('/overview') || url.includes('/webview')) {
            console.log('final send to forgerock login !');
            return this.sendToForgerockLogin(url);
          } else {
            console.log('send to myaccount signin page');
            return this.sendToLogin(url);
          }
        }
      }
    } catch (err) {
      this.analyticsFacade.trackEvent({
        event: 'secure_sign_in_failure',
        error_code: 'NOAUTH'
      });
      console.log('send to myaccount signin page from catch');
      return this.sendToLogin(url);
    }
  }

  private sendToLogin(url: string): boolean {
    this.setPendingRedirect(url);
    this.forgerockAuthService.redirectUrl = url;
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/login']
      })
    );
    return false;
  }
  private sendToEmailValidation(url: string): boolean {
    this.forgerockAuthService.redirectUrl = url;

    localStorage.setItem('emailTokenUrl', url);

    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/email-validation']
      })
    );
    return false;
  }
  private dispatchToAuthenticate(tokens: OAuth2Tokens): boolean {
    this.analyticsFacade.trackEvent({
      event: 'authenticate_sign_in_start'
    });
    this.spinner.start();
    console.log('authenticate with tokens for custkey');
    this.store.dispatch(
      forgerockLogin({ accessToken: tokens.accessToken, idToken: tokens.idToken })
    );
    return true;
  }

  private setPendingRedirect(url: string) {
    const deepLinks: string[] = this.copyService.getCopy('auth.deepLinks');
    const key = this.copyService.getCopy('auth.pendingRedirectKey');
    const isDeepLink = deepLinks.some(deepLink => url.includes(deepLink));
    if (isDeepLink) {
      sessionStorage.setItem(key, url);
    }
  }

  private async sendToForgerockLogin(url) {
    this.spinner.start();
    this.forgerockAuthService.redirectUrl = url;
    const acrValues = env.forgerock.login;
    const tokens = await this.forgerockAuthService.getTokens(acrValues, false);
    if (tokens) return this.dispatchToAuthenticate(tokens as OAuth2Tokens);
  }

  private async hasTokens(): Promise<boolean> {
    const tokens = await TokenStorage.get();
    return !!(tokens as OAuth2Tokens)?.accessToken;
  }
}
